import axios from 'axios';
import URLS from '../config/urls';
import type { 
  Category, 
  Subcategory, 
  Year, 
  Winner, 
  BlogPost, 
  Partner, 
  Testimonial, 
  NewsletterSubscription,
  User,
  AuthResponse,
  LoginCredentials,
  RegisterData,
  VerificationData,
  NominationCategory,
  NominationData,
  Nomination,
  VotingCategory,
  VotingNominee,
  Vote,
  VoteData,
  Statistics
} from '../types';

const api = axios.create({
  baseURL: URLS.API.BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Simple in-memory cache to prevent duplicate requests
const cache = new Map<string, { data: unknown; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Helper function to get cached data or fetch new data
const getCachedOrFetch = async <T>(cacheKey: string, fetchFn: () => Promise<T>): Promise<T> => {
  const now = Date.now();
  const cached = cache.get(cacheKey);
  
  if (cached && (now - cached.timestamp) < CACHE_DURATION) {
    return cached.data as T;
  }
  
  const data = await fetchFn();
  cache.set(cacheKey, { data, timestamp: now });
  return data;
};

// Categories API
export const categoriesApi = {
  getAll: async (): Promise<Category[]> => {
    return getCachedOrFetch('categories:all', async () => {
      const response = await api.get('/categories');
      return response.data.data; // Laravel API returns {success, data, message}
    });
  },
  
  getById: async (id: number): Promise<Category> => {
    return getCachedOrFetch(`categories:${id}`, async () => {
      const response = await api.get(`/categories/${id}`);
      return response.data.data;
    });
  },
  
  getSubcategories: async (categoryId: number): Promise<Subcategory[]> => {
    return getCachedOrFetch(`categories:${categoryId}:subcategories`, async () => {
      const response = await api.get(`/categories/${categoryId}/subcategories`);
      return response.data.data;
    });
  },
  
  getYears: async (): Promise<string[]> => {
    return getCachedOrFetch('categories:years', async () => {
      const response = await api.get('/categories/years');
      return response.data.data;
    });
  },
};

// Years API (using categories endpoint)
export const yearsApi = {
  getAll: async (): Promise<Year[]> => {
    const response = await api.get('/categories/years');
    return response.data.data.map((year: string) => ({ id: parseInt(year), year }));
  },
};

// Winners API
export const winnersApi = {
  getAll: async (filters?: { year?: string; category?: string; subcategory?: string; status?: string }): Promise<Winner[]> => {
    const params = new URLSearchParams();
    if (filters?.year) params.append('year', filters.year);
    if (filters?.category) params.append('category', filters.category);
    if (filters?.subcategory) params.append('subcategory', filters.subcategory);
    if (filters?.status) params.append('status', filters.status);
    
    const queryString = params.toString();
    const cacheKey = `winners:all:${queryString}`;
    
    return getCachedOrFetch(cacheKey, async () => {
      const url = queryString ? `/winners?${queryString}` : '/winners';
      const response = await api.get(url);
      return response.data.data;
    });
  },
  
  getById: async (id: number): Promise<Winner> => {
    return getCachedOrFetch(`winners:${id}`, async () => {
      const response = await api.get(`/winners/${id}`);
      return response.data.data;
    });
  },
  
  getByYear: async (year: string): Promise<Record<string, Winner[]>> => {
    return getCachedOrFetch(`winners:year:${year}`, async () => {
      const response = await api.get(`/winners/year/${year}`);
      return response.data.data;
    });
  },
  
  getByCategory: async (category: string, year?: string): Promise<Winner[]> => {
    const cacheKey = year ? `winners:category:${category}:${year}` : `winners:category:${category}`;
    return getCachedOrFetch(cacheKey, async () => {
      const params = year ? `?year=${year}` : '';
      const response = await api.get(`/winners/category/${category}${params}`);
      return response.data.data;
    });
  },
  
  getYears: async (): Promise<string[]> => {
    return getCachedOrFetch('winners:years', async () => {
      const response = await api.get('/winners/years');
      return response.data.data;
    });
  },
  
  getCategories: async (): Promise<string[]> => {
    return getCachedOrFetch('winners:categories', async () => {
      const response = await api.get('/winners/categories');
      return response.data.data;
    });
  },
  
  getSubcategories: async (category?: string): Promise<string[]> => {
    const cacheKey = category ? `winners:subcategories:${category}` : 'winners:subcategories';
    return getCachedOrFetch(cacheKey, async () => {
      const params = category ? `?category=${category}` : '';
      const response = await api.get(`/winners/subcategories${params}`);
      return response.data.data;
    });
  },
};

// Blog API (Public)
export const blogApi = {
  getAll: async (params?: { search?: string; tag?: string; author?: string; per_page?: number }): Promise<BlogPost[]> => {
    const response = await api.get('/blog', { params });
    return response.data.data;
  },
  
  getBySlug: async (slug: string): Promise<BlogPost> => {
    const response = await api.get(`/blog/${slug}`);
    return response.data.data;
  },
  
  getFeatured: async (limit: number = 3): Promise<BlogPost[]> => {
    const response = await api.get(`/blog/featured?limit=${limit}`);
    return response.data.data;
  },
  
  getRecent: async (limit: number = 5): Promise<BlogPost[]> => {
    const response = await api.get(`/blog/recent?limit=${limit}`);
    return response.data.data;
  },
  
  getTags: async (): Promise<string[]> => {
    const response = await api.get('/blog/tags');
    return response.data.data;
  },
  
  getAuthors: async (): Promise<string[]> => {
    const response = await api.get('/blog/authors');
    return response.data.data;
  },
};

// Admin Blog API
export const adminBlogApi = {
  getAll: async (params?: { status?: string; search?: string; per_page?: number }): Promise<BlogPost[]> => {
    const response = await api.get('/admin/blog', { params });
    return response.data.data;
  },
  
  getById: async (id: number): Promise<BlogPost> => {
    const response = await api.get(`/admin/blog/${id}`);
    return response.data.data;
  },
  
  create: async (post: Partial<Omit<BlogPost, 'id' | 'views' | 'created_at' | 'updated_at' | 'slug'>> & {
    title: string;
    excerpt: string;
    content: string;
    author: string;
    status: 'draft' | 'published' | 'archived';
  }): Promise<BlogPost> => {
    const response = await api.post('/admin/blog', post);
    return response.data.data;
  },
  
  update: async (id: number, post: Partial<BlogPost>): Promise<BlogPost> => {
    const response = await api.put(`/admin/blog/${id}`, post);
    return response.data.data;
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/admin/blog/${id}`);
  },
  
  uploadImage: async (file: File): Promise<{ url: string; path: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.post('/admin/blog/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data.data;
  },
  
  updateStatus: async (id: number, status: 'draft' | 'published' | 'archived'): Promise<BlogPost> => {
    const response = await api.patch(`/admin/blog/${id}/status`, { status });
    return response.data.data;
  },
  
  duplicate: async (id: number): Promise<BlogPost> => {
    const response = await api.post(`/admin/blog/${id}/duplicate`);
    return response.data.data;
  },
};

// Partners API
export const partnersApi = {
  getAll: async (): Promise<Partner[]> => {
    const response = await api.get('/partners');
    return response.data;
  },
};

// Testimonials API
export const testimonialsApi = {
  getAll: async (): Promise<Testimonial[]> => {
    const response = await api.get('/testimonials');
    return response.data;
  },
};

// Newsletter API
export const newsletterApi = {
  subscribe: async (data: NewsletterSubscription): Promise<void> => {
    await api.post('/newsletter/subscribe', data);
  },
};

// Authentication API
export const authApi = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },
  
  register: async (data: RegisterData): Promise<{ message: string }> => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },
  
  verifyEmail: async (data: VerificationData): Promise<AuthResponse> => {
    const response = await api.post('/auth/verify-email', data);
    return response.data;
  },
  
  resendVerificationCode: async (email: string): Promise<{ message: string }> => {
    const response = await api.post('/auth/resend-verification', { email });
    return response.data;
  },
  
  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },
  
  resetPassword: async (token: string, password: string, passwordConfirmation: string): Promise<{ message: string }> => {
    const response = await api.post('/auth/reset-password', { token, password, password_confirmation: passwordConfirmation });
    return response.data;
  },
  
  getUser: async (): Promise<User> => {
    const response = await api.get('/auth/user');
    return response.data;
  },
  
  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
  },
};

// Nomination API
export const nominationApi = {
  getCategories: async (): Promise<NominationCategory[]> => {
    const response = await api.get('/nominations/categories');
    return response.data;
  },
  
  submitNomination: async (data: NominationData): Promise<{ message: string, nomination: Nomination }> => {
    // Create FormData for file uploads
    const formData = new FormData();
    formData.append('categoryId', data.categoryId.toString());
    
    if (data.subcategoryId) {
      formData.append('subcategoryId', data.subcategoryId.toString());
    }
    
    formData.append('nominee', JSON.stringify(data.nominee));
    formData.append('justification', data.justification);
    formData.append('achievements', data.achievements);
    formData.append('supportingLinks', JSON.stringify(data.supportingLinks));
    formData.append('consentToUseInfo', data.consentToUseInfo.toString());
    
    // Append files if any
    if (data.supportingFiles && data.supportingFiles.length > 0) {
      data.supportingFiles.forEach((file, index) => {
        formData.append(`supportingFiles[${index}]`, file);
      });
    }
    
    const response = await api.post('/nominations/submit', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  },
  
  getUserNominations: async (): Promise<Nomination[]> => {
    const response = await api.get('/nominations/user');
    return response.data;
  },
  
  getNominationDetails: async (id: number): Promise<Nomination> => {
    const response = await api.get(`/nominations/${id}`);
    return response.data;
  },
};

// Voting API
export const votingApi = {
  getCategories: async (): Promise<VotingCategory[]> => {
    const response = await api.get('/voting/categories');
    return response.data;
  },
  
  getNominees: async (categoryId: number): Promise<VotingNominee[]> => {
    const response = await api.get(`/voting/categories/${categoryId}/nominees`);
    return response.data;
  },
  
  getNomineeDetails: async (nomineeId: number): Promise<VotingNominee> => {
    const response = await api.get(`/voting/nominees/${nomineeId}`);
    return response.data;
  },
  
  castVote: async (data: VoteData): Promise<{ message: string, vote: Vote }> => {
    const response = await api.post('/voting/vote', data);
    return response.data;
  },
  
  getUserVotes: async (): Promise<Vote[]> => {
    const response = await api.get('/voting/my-votes');
    return response.data;
  },
  
  checkIfVoted: async (categoryId: number): Promise<{ hasVoted: boolean, nomineeId?: number }> => {
    const response = await api.get(`/voting/categories/${categoryId}/check-vote`);
    return response.data;
  },
};

// Statistics API
export const statisticsApi = {
  getAll: async (): Promise<Statistics> => {
    return getCachedOrFetch('statistics:all', async () => {
      const response = await api.get('/statistics');
      return response.data.data;
    });
  },
};

// Add authorization header for protected routes
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export default api;
